import capitalize from 'lodash/capitalize';
import api from 'services/api';
import { expandString } from 'utils/string';
import { getConfigFile } from './theme-config';

const PAGE_ID_PREFIX = 'page.';
const PAGE_PATH_REGEXP = /theme\/templates\/page\.([\w-]+)\.json/;

function validatePageData(pageData) {
  if (!pageData) {
    throw new Error('Page data is not defined');
  }
}

export function getSlugFromPageData(pageData) {
  validatePageData(pageData);

  const slug = pageData.page?.slug;

  if (!slug) {
    throw new Error('Page slug is not defined');
  }

  return slug;
}

export function getIdFromPageData(pageData) {
  validatePageData(pageData);

  const { id } = pageData;

  if (!id) {
    throw new Error('Page ID is not defined');
  }

  return id;
}

export function getPageId(slug) {
  const hasSlug = slug?.length > 0;

  return hasSlug ? `${PAGE_ID_PREFIX}${slug}` : null;
}

export function getPageSlug(pageId) {
  if (!pageId) {
    return null;
  }

  return pageId?.replace(PAGE_ID_PREFIX, '');
}

export function getPageUrl(slug) {
  return `/pages/${slug}`;
}

export function getPageFile(slug, fileData) {
  const name = getPageId(slug);

  return getConfigFile(name, fileData);
}

export function getNewPageData(values) {
  const { label, slug, description } = values;

  return {
    sections: {},
    order: [],
    page: {
      slug,
      label,
      url: getPageUrl(slug),
      description,
    },
    id: getPageId(slug),
  };
}

export function findPage(pages, key, value) {
  for (const page of pages) {
    if (!page) {
      continue;
    }

    if (page[key] === value) {
      return page;
    }

    const { items } = page;
    const hasItems = items?.length > 0;

    if (!hasItems) {
      continue;
    }

    const foundPage = findPage(page.items, key, value);

    if (foundPage) {
      return foundPage;
    }
  }
}

export async function getPages(editorConfig, pageConfigs) {
  const templateConfigs = await getPageTemplateConfigs(editorConfig);
  const groupedTemplateConfigs = groupConfigs(templateConfigs);
  const { index: home, ...templates } = groupedTemplateConfigs;
  const compiledPageConfigs = getPageConfigs(pageConfigs);

  const configs = {
    templates: Object.values(templates),
    pages: [home, ...compiledPageConfigs],
  };

  return setActions(configs);
}

function setActions(configs) {
  // disable actions for templates
  const templates = configs.templates.map((template) => {
    const hasItems = template.items?.length > 0;

    return {
      ...template,
      ...(hasItems && {
        items: template.items.map((item) => ({ ...item, actions: false })),
      }),
      actions: false,
    };
  });

  const [home, ...pages] = configs.pages;

  // disable 'delete' action for home page
  home.actions = ['duplicate', 'settings'];

  return { templates, pages: [home, ...pages] };
}

function getPageConfigs(pageConfigs) {
  return pageConfigs.results.reduce((acc, config) => {
    if (!config) {
      return acc;
    }

    const { file_path: path, file_data: data } = config;
    const hasPath = path?.length > 0;

    if (!hasPath) {
      return acc;
    }

    const match = path.match(PAGE_PATH_REGEXP);

    if (!match) {
      return acc;
    }

    const [, slug] = match;

    let configData;

    try {
      configData = JSON.parse(data);
    } catch {
      return acc;
    }

    const id = getPageId(slug);
    const { page } = configData || {};
    const hasId = id?.length > 0;
    const hasLabel = page?.label?.length > 0;
    const shouldAdd = hasId && hasLabel;

    if (shouldAdd) {
      acc.push({
        ...page,
        id,
        custom: true,
      });
    }

    return acc;
  }, []);
}

function groupConfigs(configs) {
  return configs.reduce((acc, config) => {
    const { id, group } = config;

    if (!group) {
      acc[id] = { ...config };

      return acc;
    }

    if (acc[group]) {
      acc[group].items.push(config);
    } else {
      acc[group] = {
        id: group,
        label: getGroupLabel(group),
        items: [config],
      };
    }

    return acc;
  }, {});
}

function getGroupLabel(group) {
  switch (group) {
    case 'customer':
      return 'Customer portal';
    default:
      return capitalize(group);
  }
}

async function getPageTemplateConfigs(editorConfig) {
  const pageConfigs = editorConfig?.globals?.storefrontConfig?.pages || [];
  // TODO: revisit this approach in the future
  const contentPageConfigs = []; // await getContentPageConfigs();
  const allConfigs = [...pageConfigs, ...contentPageConfigs];
  const configs = [];

  for await (const config of allConfigs) {
    if (!config?.label) {
      continue;
    }

    if (false && config.collection) {
      const collectionPage = await getCollectionConfig(config);

      configs.push(collectionPage);
    } else {
      configs.push(config);
    }
  }

  return configs;
}

async function getContentPageConfigs() {
  // TODO: get these from content model reducer instead
  const storefrontModels = await api.get('/data/:models', {
    'storefront.enabled': true,
    public: true,
    limit: 100,
  });

  return storefrontModels.results.reduce((acc, model) => {
    const listSlug =
      model.storefront?.list_slug || model.collection.replace('_', '/');
    if (model.storefront?.list) {
      acc.push({
        id: `content/${listSlug}/list`,
        label: `${model.label} list`, // TODO: singularize
        url: `/pages/${listSlug}`,
        group: 'content',
      });
    }
    if (model.storefront?.page) {
      acc.push({
        id: `content/${listSlug}/entry`,
        label: model.label, // TODO: singularize
        url: `/pages/${listSlug}/{slug}`,
        group: 'content',
        collection: model.collection,
        slug_field: model.storefront?.page_slug_field,
      });
    }

    return acc;
  }, []);
}

async function getCollectionConfig(config) {
  try {
    const url = await getCollectionConfigRecordUrl(config);

    return {
      ...config,
      url,
    };
  } catch (error) {
    console.error(error);

    return {
      ...config,
      url: false,
    };
  }
}

async function getCollectionConfigRecordUrl(config) {
  const record = await getCollectionConfigRecord(config);

  return record ? expandString(config.url, record) : false;
}

function getCollectionConfigRecord(config) {
  switch (config.collection) {
    case 'content/blogs':
      return getContentBlogRecord();
    default:
      return getCollectionRecord(config);
  }
}

async function getContentBlogRecord() {
  const record = await api.get('/data/content/blogs/:last', {
    category_id: { $ne: null },
    expand: 'category',
  });
  const categorySlug = record?.category?.slug;

  if (categorySlug) {
    record.category = categorySlug;
  }

  return record;
}

async function getCollectionRecord(config) {
  const { collection, slug_field: slugField } = config;

  const record = await api.get('/data/{collection}/:last', {
    collection,
  });
  const recordSlug = slugField ? record?.[slugField] : null;

  if (recordSlug) {
    record.slug = recordSlug;
  }

  return record;
}
